"use client";

import { useCallback } from "react";
import { useQueryParamsAdapter } from "./queryParamsAdapter";
import { useQueryParamsNavigate } from "./useQueryParamsNavigate";
import {
  getGlobalFilterKey,
  type GlobalFilterField,
} from "../../utils/queryParams/globalQueryParams";

const noopGlobalQueryParams = {
  isPending: false,
  setFilter: () => {},
  removeFilter: () => {},
  getFilterValue: () => null,
  getRawParam: () => null,
  setRawParam: () => {},
  updateParams: () => {},
  clearAll: () => {},
} satisfies ReturnType<typeof useGlobalQueryParams>;

export const useNoopGlobalQueryParams = () => noopGlobalQueryParams;

export function useGlobalQueryParams() {
  const { navigate, isPending } = useQueryParamsNavigate();
  const { searchParams } = useQueryParamsAdapter();

  const setFilter = useCallback(
    (field: GlobalFilterField, value: string | null) => {
      navigate((params) => {
        const key = getGlobalFilterKey(field);
        value == null ? params.delete(key) : params.set(key, value);
      });
    },
    [navigate],
  );

  const removeFilter = useCallback(
    (field: GlobalFilterField) => {
      navigate((params) => params.delete(getGlobalFilterKey(field)));
    },
    [navigate],
  );

  const getFilterValue = useCallback(
    (field: GlobalFilterField): string | null => {
      return searchParams.get(getGlobalFilterKey(field));
    },
    [searchParams],
  );

  const getRawParam = useCallback(
    (key: string): string | null => searchParams.get(key),
    [searchParams],
  );

  const updateParams = useCallback(
    (updater: (params: URLSearchParams) => void) => navigate(updater),
    [navigate],
  );

  const setRawParam = useCallback(
    (key: string, value: string | null) => {
      navigate((params) => {
        value == null ? params.delete(key) : params.set(key, value);
      });
    },
    [navigate],
  );

  const clearAll = useCallback(() => {
    navigate((params) => {
      Array.from(params.keys())
        .filter((k) => k.startsWith("filter["))
        .forEach((k) => params.delete(k));
    });
  }, [navigate]);

  return {
    isPending,
    setFilter,
    removeFilter,
    getFilterValue,
    getRawParam,
    setRawParam,
    updateParams,
    clearAll,
  };
}
